// src/contexts/SubscriptionContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import useAuthReset from '../hooks/useAuthReset';
import api from '../services/api';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

// Hardcoded fallback used if the /feature-flags/effective endpoint is
// unavailable. Keep this in sync with the server-side defaults so that
// gating still works when the feature-flag service is offline.
// Plan hierarchy: free < standard < premium < family < business
const FALLBACK_FEATURE_MAP = {
  // Available to all plans
  basicBudgeting:       ['free', 'standard', 'premium', 'family', 'business'],
  expenseTracking:      ['free', 'standard', 'premium', 'family', 'business'],
  basicReports:         ['free', 'standard', 'premium', 'family', 'business'],

  // Standard and above
  customReports:        ['standard', 'premium', 'family', 'business'],
  dataExport:           ['standard', 'premium', 'family', 'business'],
  advancedTracking:     ['standard', 'premium', 'family', 'business'],

  // Premium and above
  aiInsights:           ['premium', 'family', 'business'],
  advancedAnalytics:    ['premium', 'family', 'business'],
  prioritySupport:      ['premium', 'family', 'business'],

  // Family and above
  multiUser:            ['family', 'business'],
  householdAnalytics:   ['family', 'business'],
  sharedBudgets:        ['family', 'business'],

  // Business only
  businessFeatures:     ['business'],
  apiAccess:            ['business'],

  // AI receipt features
  receiptAI:            ['premium', 'family', 'business'],
  merchantAnalytics:    ['standard', 'premium', 'family', 'business'],

  // Tier rebalance — Standard tier value-adds
  savingsGoals:              ['standard', 'premium', 'family', 'business'],
  debtPayoff:                ['standard', 'premium', 'family', 'business'],
  automationRules:           ['standard', 'premium', 'family', 'business'],
  receiptCategorySuggestion: ['standard', 'premium', 'family', 'business'],
  receiptReanalyse:          ['standard', 'premium', 'family', 'business'],

  // Tier rebalance — Premium-and-above intelligence features
  budgetForecasting:         ['premium', 'family', 'business'],
  cashflowForecast:          ['premium', 'family', 'business'],
  spendingAnomalyAlerts:     ['premium', 'family', 'business'],
  pdfBrandedReports:         ['premium', 'family', 'business'],
  payables:                  ['premium', 'family', 'business'],
  pantry:                    ['premium', 'family', 'business'],
  paycheckPlan:              ['premium', 'family', 'business'],
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // featureMap is fetched from the server (Wave A: /api/feature-flags/effective).
  // Null means "not yet loaded" — hasFeature falls back to FALLBACK_FEATURE_MAP.
  const [featureMap, setFeatureMap] = useState(null);

  // Privacy guard: clear cached subscription tier when the authenticated
  // user changes. Otherwise a Premium User A's plan flags would briefly
  // unlock features for Free User B on a shared device.
  const resetSubscriptionState = useCallback(() => {
    setSubscription(null);
    setError(null);
    setFeatureMap(null);
  }, []);
  useAuthReset(resetSubscriptionState);

  // Fetch user's subscription when authenticated
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!isAuthenticated) {
        setSubscription(null);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await api.get('/subscription');
        // API returns { success, data: { subscription: {...} } } — extract the subscription object
        const sub = response.data?.data?.subscription || response.data?.data || response.data;
        setSubscription(sub);
        setError(null);
      } catch (err) {
        // Silently handle subscription fetch errors (service may not be available yet)
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('Subscription service unavailable, using default free plan:', err.response?.status);
        }
        // Set a default free plan if we can't fetch subscription
        setSubscription({ plan: 'free', status: 'active' });
        setError(null); // Don't show error to user, just use default
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscription();
  }, [isAuthenticated, user]);

  // Fetch the dynamic feature map once auth + subscription are ready.
  // Server (Wave A) sets Cache-Control: max-age=60 so we rely on the browser
  // cache rather than scheduling a client-side timer.
  useEffect(() => {
    if (!isAuthenticated || !user || loading) return;

    let cancelled = false;
    const fetchFeatureMap = async () => {
      try {
        const response = await api.get('/feature-flags/effective');
        // Accept either { data: {...} } envelope or the bare map.
        const payload = response.data?.data ?? response.data;
        if (!cancelled && payload && typeof payload === 'object') {
          setFeatureMap(payload);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'Feature flags endpoint unavailable, using hardcoded fallback:',
            err.response?.status || err.message
          );
        }
        if (!cancelled) {
          // Explicitly leave featureMap as null so hasFeature uses fallback.
          setFeatureMap(null);
        }
      }
    };

    fetchFeatureMap();
    return () => { cancelled = true; };
    // user?.id captures auth identity changes (login as different user).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, loading]);

  // Check if a feature is available for the current subscription.
  // Synchronous: reads from state featureMap if loaded, otherwise fallback.
  const hasFeature = (featureName) => {
    if (!subscription) return false;
    const plan = String(subscription.plan || '').toLowerCase();
    const tiers = (featureMap && Object.prototype.hasOwnProperty.call(featureMap, featureName))
      ? featureMap[featureName]
      : FALLBACK_FEATURE_MAP[featureName];
    return tiers?.includes(plan) || false;
  };

  // Check if user is within the usage limits of their plan
  // Pass currentCount to compare against the plan limit
  const isWithinPlanLimit = (resourceType, currentCount = null) => {
    if (!subscription) return true; // optimistic default
    const limits = {
      free:     { transactions: 50, receipts: 3, splitBills: 0, budgetCategories: 5 },
      standard: { transactions: 200, receipts: 50, splitBills: 10, budgetCategories: 15 },
      premium:  { transactions: -1, receipts: 200, splitBills: -1, budgetCategories: -1 },
      family:   { transactions: -1, receipts: 500, splitBills: -1, budgetCategories: -1 },
      business: { transactions: -1, receipts: -1,  splitBills: -1, budgetCategories: -1 },
    };
    const planLimits = limits[subscription.plan] || limits.free;
    const limit = planLimits[resourceType];
    if (limit === -1 || limit === undefined) return true; // unlimited
    if (currentCount !== null) return currentCount < limit;
    return true; // no count provided — defer to server
  };

  // Get the numeric limit for a resource type (-1 = unlimited)
  const getPlanLimit = (resourceType) => {
    if (!subscription) return 5; // safe default
    const limits = {
      free:     { transactions: 50, receipts: 3, splitBills: 0, budgetCategories: 5 },
      standard: { transactions: 200, receipts: 50, splitBills: 10, budgetCategories: 15 },
      premium:  { transactions: -1, receipts: 200, splitBills: -1, budgetCategories: -1 },
      family:   { transactions: -1, receipts: 500, splitBills: -1, budgetCategories: -1 },
      business: { transactions: -1, receipts: -1,  splitBills: -1, budgetCategories: -1 },
    };
    const planLimits = limits[subscription.plan] || limits.free;
    return planLimits[resourceType] ?? -1;
  };
  
  const value = {
    subscription,
    loading,
    error,
    hasFeature,
    isWithinPlanLimit,
    getPlanLimit
  };
  
  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};